import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  ArrowPathIcon,
  ArrowRightIcon,
  ArrowsPointingOutIcon,
  ArrowUpTrayIcon,
  CheckIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { AdManagementService, type ReferenceImage } from '../../client'
import { anchoredStyle } from '../../lib/anchor'
import { useReferenceLibrary } from './useReferenceLibrary'

const MAX_IMAGES = 500
const MAX_SELECTED = 10
const SCRAPE_WIDTH = 288
const SEARCH_WIDTH = 320
const POPUP_HEIGHT = 300

const ACTION = 'flex items-center gap-1 rounded-md px-2.5 py-1 text-xs transition disabled:opacity-50'
const ACTION_IDLE = 'text-gray-500 hover:bg-gray-100'
const ACTION_ON = 'bg-purple-50 text-purple-700'
const POPUP = 'fixed z-[9999] rounded-lg border border-gray-200 bg-white p-3 shadow-xl'
const CORNER =
  'absolute flex size-6 items-center justify-center rounded-full bg-black/70 opacity-0 transition group-hover:opacity-100'

type Popup = { kind: 'scrape' | 'search'; anchor: DOMRect }

type Props = {
  brandId: number
  images: ReferenceImage[]
  selected: string[]
  websiteUrl?: string
  onImagesChange: (images: ReferenceImage[]) => void
  onSelectedChange: (urls: string[]) => void
  onPreview: (url: string) => void
}

export default function ReferenceImageManager({
  brandId,
  images,
  selected,
  websiteUrl,
  onImagesChange,
  onSelectedChange,
  onPreview,
}: Props) {
  const [popup, setPopup] = useState<Popup | null>(null)
  const [scrapeUrl, setScrapeUrl] = useState(websiteUrl ?? '')
  const [scraping, setScraping] = useState(false)
  const popupRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const library = useReferenceLibrary({
    brandId,
    images,
    selected,
    maxSelected: MAX_SELECTED,
    onImagesChange,
    onSelectedChange,
  })

  const full = images.length >= MAX_IMAGES
  const fullTitle = full ? `Maximum of ${MAX_IMAGES} reference images reached` : undefined

  useEffect(() => {
    function onPointerDown(event: MouseEvent) {
      const target = event.target as Node
      if (popupRef.current?.contains(target)) return
      // the trigger toggles the popup itself; closing here too would immediately reopen it
      if ((target as HTMLElement).closest?.('[data-reference-action]')) return
      setPopup(null)
    }

    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [])

  async function scrape() {
    if (!scrapeUrl.trim() || scraping) return

    setScraping(true)
    library.setError('')
    try {
      const { new_images } = await AdManagementService.scrapeMoreImages(brandId, scrapeUrl.trim())
      if (new_images?.length) onImagesChange([...new_images, ...images])
      else library.setError('No new images found on that website')
      setPopup(null)
    } catch {
      library.setError('Failed to fetch images from that website')
    } finally {
      setScraping(false)
    }
  }

  function openPopup(kind: Popup['kind'], event: React.MouseEvent<HTMLButtonElement>) {
    const anchor = event.currentTarget.getBoundingClientRect()
    setPopup((current) => (current?.kind === kind ? null : { kind, anchor }))
  }

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700">Choose reference images</h3>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            data-reference-action
            disabled={scraping || full}
            title={fullTitle}
            onClick={(event) => openPopup('scrape', event)}
            className={`${ACTION} ${popup?.kind === 'scrape' ? ACTION_ON : ACTION_IDLE}`}
          >
            <ArrowPathIcon className={`size-3.5 ${scraping ? 'animate-spin' : ''}`} />
            Import from Website
          </button>

          <button
            type="button"
            disabled={library.uploading || full}
            title={fullTitle}
            onClick={() => fileInputRef.current?.click()}
            className={`${ACTION} ${ACTION_IDLE}`}
          >
            {library.uploading ? (
              <span className="size-3.5 animate-spin rounded-full border-2 border-gray-300 border-t-gray-500" />
            ) : (
              <ArrowUpTrayIcon className="size-3.5" />
            )}
            Upload Image
          </button>

          <button
            type="button"
            data-reference-action
            disabled={full}
            title={fullTitle}
            onClick={(event) => openPopup('search', event)}
            className={`${ACTION} ${popup?.kind === 'search' ? ACTION_ON : ACTION_IDLE}`}
          >
            <MagnifyingGlassIcon className="size-3.5" />
            Search Web
          </button>
        </div>
      </div>

      {images.length > 0 ? (
        <div className="flex max-h-44 flex-wrap gap-2 overflow-y-auto [scrollbar-width:thin]">
          {images.map(({ gcs_url }) => {
            const isSelected = selected.includes(gcs_url)
            return (
              <div
                key={gcs_url}
                onClick={() => library.toggle(gcs_url)}
                className={`group relative size-20 shrink-0 cursor-pointer overflow-hidden rounded-lg border-2 transition ${
                  isSelected ? 'border-purple-900 ring-2 ring-purple-200' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <img src={gcs_url} alt="" className="size-full object-cover" />
                {isSelected && (
                  <>
                    <span className="pointer-events-none absolute inset-0 bg-purple-900/10" />
                    <span className="pointer-events-none absolute top-1 left-1 flex size-4 items-center justify-center rounded-full bg-purple-900">
                      <CheckIcon className="size-2.5 text-white" strokeWidth={3} />
                    </span>
                  </>
                )}
                <button
                  type="button"
                  aria-label="Remove image"
                  onClick={(event) => {
                    event.stopPropagation()
                    void library.remove(gcs_url)
                  }}
                  className={`${CORNER} top-1 left-1`}
                >
                  <XMarkIcon className="size-3.5 text-white" />
                </button>
                <button
                  type="button"
                  aria-label="Preview image"
                  onClick={(event) => {
                    event.stopPropagation()
                    onPreview(gcs_url)
                  }}
                  className={`${CORNER} top-1 right-1`}
                >
                  <ArrowsPointingOutIcon className="size-3 text-white" />
                </button>
              </div>
            )
          })}
        </div>
      ) : (
        <p className="text-xs text-gray-400">No reference images yet</p>
      )}

      {library.error && <p className="mt-2 text-[11px] text-red-500">{library.error}</p>}

      {popup &&
        createPortal(
          <div
            ref={popupRef}
            style={anchoredStyle(popup.anchor, {
              width: popup.kind === 'scrape' ? SCRAPE_WIDTH : SEARCH_WIDTH,
              height: POPUP_HEIGHT,
              align: popup.kind === 'scrape' ? 'start' : 'end',
              gap: 6,
            })}
            className={`${POPUP} ${popup.kind === 'search' ? 'space-y-2' : ''}`}
          >
            {popup.kind === 'scrape' ? (
              <div className="relative">
                <input
                  autoFocus
                  type="url"
                  value={scrapeUrl}
                  onChange={(event) => setScrapeUrl(event.target.value)}
                  onKeyDown={(event) => event.key === 'Enter' && void scrape()}
                  placeholder="https://yourbrand.com"
                  className="w-full rounded-lg border border-gray-300 py-1.5 pr-8 pl-3 text-sm focus:ring-2 focus:ring-purple-900 focus:outline-none"
                />
                <button
                  type="button"
                  aria-label="Import from website"
                  disabled={!scrapeUrl.trim() || scraping}
                  onClick={() => void scrape()}
                  className="absolute top-1/2 right-2.5 -translate-y-1/2 disabled:opacity-40"
                >
                  <ArrowRightIcon className="size-4 text-gray-400" />
                </button>
              </div>
            ) : (
              <>
                <div className="relative">
                  <input
                    autoFocus
                    value={library.query}
                    onChange={(event) => library.search(event.target.value)}
                    placeholder="Search stock photos..."
                    className="w-full rounded-lg border border-gray-300 py-1.5 pr-8 pl-3 text-sm focus:ring-2 focus:ring-purple-900 focus:outline-none"
                  />
                  <MagnifyingGlassIcon className="absolute top-1/2 right-2.5 size-4 -translate-y-1/2 text-gray-400" />
                </div>

                {library.searching ? (
                  <div className="flex justify-center py-3">
                    <span className="size-5 animate-spin rounded-full border-2 border-purple-200 border-t-purple-600" />
                  </div>
                ) : library.results.length > 0 ? (
                  <div className="grid max-h-[200px] grid-cols-4 gap-1.5 overflow-y-auto [scrollbar-width:thin]">
                    {library.results.map((image) => (
                      <button
                        key={image.url}
                        type="button"
                        disabled={library.importing === image.url}
                        title={`Photo by ${image.photographer}`}
                        onClick={() =>
                          void library.importImage(image).then((ok) => {
                            if (ok) setPopup(null)
                          })
                        }
                        className="group relative aspect-square overflow-hidden rounded-lg border border-gray-200 transition hover:border-purple-400 focus:outline-none"
                      >
                        <img src={image.thumbnail} alt="" className="size-full object-cover" />
                        <span className="absolute inset-0 flex items-center justify-center transition group-hover:bg-black/20">
                          <PlusIcon className="size-5 text-white opacity-0 drop-shadow transition group-hover:opacity-100" />
                        </span>
                      </button>
                    ))}
                  </div>
                ) : library.searched ? (
                  <p className="py-2 text-center text-xs text-gray-400">No results found</p>
                ) : null}
              </>
            )}
          </div>,
          document.body,
        )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0]
          event.target.value = ''
          if (file) void library.upload(file)
        }}
      />
    </div>
  )
}
